import React, { useState, useEffect } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { Checkbox, Form, FormButtons, Input, Label, RadioButtonGroup, Spacing, Tooltip } from '@netfront/ui-library';
import PropTypes from 'prop-types';

import { useToast } from '../../../../../../hooks';
import client from '../../../../../../middleware/client';
import { CREATE_ACTION_QUERY_MAP } from '../../../ActionTab.constants';
import { getEventValue, getEventChecked } from '../../../helpers';
import { useConditionLocations, useConditionAdd } from '../../../hooks';
import {
  NOTIFICATION_DISPLAY_TIME_IN_MILLISECONDS,
  QUESTION_ACTION_PROP_TYPE,
  QUESTION_LOCATION_PROP_TYPE,
  SELECTED_CONDITION_QUESTION_PROP_TYPE,
  WRAPPER_PROP_TYPE,
} from '../../QuestionConditions.constants';

import '../../../ActionTab.css';

const DEFAULT_SLIDER_CONDITION = {
  contentPageId: undefined,
  contentSnippetQuestionId: undefined,
  isTemplate: false,
  questionActionId: undefined,
  minValue: undefined,
  maxValue: undefined,
  title: '',
};

const QuestionConditionSliderNew = ({ args }) => {
  const {
    actionWrapper,
    activePageId,
    conditionAction,
    questionAction,
    questionLocations,
    questionTitle,
    selectedConditionQuestionItem,
    onAddActionCompleted,
    onAddConditionCompleted,
    onAddQuestionConditionLocations,
    onAddSelectedQuestionItem,
    onCloseSidebar,
  } = args;

  const { handleToastError } = useToast();

  const { id: wrapperId, type: wrapper } = actionWrapper;

  const [currentAction, setCurrentAction] = useState(questionAction);
  const [isTemplateChecked, setIsTemplateChecked] = useState(false);
  const [newCondition, setNewCondition] = useState({
    ...DEFAULT_SLIDER_CONDITION,
    title: questionTitle,
  });
  const [questionSnippetForms, setQuestionSnippetForms] = useState([]);

  const createConditionVariables = {
    contentPageId: newCondition.contentPageId,
    contentSnippetQuestionId: newCondition.contentSnippetQuestionId,
    isTemplate: isTemplateChecked,
    minValue: newCondition.minValue,
    maxValue: newCondition.maxValue,
    questionActionId: questionAction ? questionAction.id : currentAction?.id,
    title: newCondition.title,
  };

  const handleAddConditionCompleted = ({ condition }) => {
    const { conditions = [] } = currentAction;

    const updatedAction = {
      ...currentAction,
      conditions: [...conditions, condition],
    };

    setTimeout(() => {
      setNewCondition(DEFAULT_SLIDER_CONDITION);
      onAddSelectedQuestionItem(undefined);

      onAddConditionCompleted(updatedAction);
    }, NOTIFICATION_DISPLAY_TIME_IN_MILLISECONDS);
  };

  const handleGetConditionLocationsCompleted = ({ getSnippetForms }) => {
    setQuestionSnippetForms(getSnippetForms);

    const conditionItemLocations = getSnippetForms.map(
      ({
        contentPage: {
          id,
          title: contentPageTitle,
          contentGroup: { title: contentGroupTitle },
        },
      }) => ({
        id,
        labelText: `${contentPageTitle} - ${contentGroupTitle}`,
        value: id.toString(),
      }),
    );

    onAddQuestionConditionLocations(conditionItemLocations);
  };

  const handleGetError = (error) => {
    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const { handleAddCondition } = useConditionAdd({
    onCompleted: handleAddConditionCompleted,
    onError: handleGetError,
    conditionType: 'slider',
    variables: createConditionVariables,
  });

  const { handleGetLocations } = useConditionLocations({
    onCompleted: handleGetConditionLocationsCompleted,
    onError: handleGetError,
  });

  const [executeCreateAction] = useMutation(CREATE_ACTION_QUERY_MAP[wrapper][conditionAction], {
    client,
    onCompleted(data) {
      const {
        questionAction: { addAction: action },
      } = data;

      setCurrentAction(action);

      onAddActionCompleted(action);

      createConditionVariables.questionActionId = action.id;

      handleAddCondition({
        createConditionVariables,
      });
    },
    onError(error) {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const handleIsTemplateChecked = (event) => {
    setIsTemplateChecked(getEventChecked(event));
  };

  const handleQuestionLocationInputChange = (event) => {
    setNewCondition({
      ...newCondition,
      contentPageId: Number(getEventValue(event)),
    });
  };

  const handleSaveCondition = (event) => {
    event.preventDefault();

    const { contentPageId, contentSnippetQuestionId, minValue, maxValue, title } = newCondition;

    if (questionAction) {
      handleAddCondition({
        createConditionVariables,
      });

      return;
    }

    if (!contentSnippetQuestionId || !minValue || !maxValue || !title || !contentPageId) {
      return;
    }

    executeCreateAction({
      variables: {
        contentPageId: activePageId,
        wrapperId,
      },
    });
  };

  const handleMinValueChange = (event) => {
    setNewCondition({
      ...newCondition,
      minValue: getEventValue(event),
    });
  };

  const handleMaxValueChange = (event) => {
    setNewCondition({
      ...newCondition,
      maxValue: getEventValue(event),
    });
  };

  useEffect(() => {
    if (!questionSnippetForms.length) {
      return;
    }

    const [firstContentSnippetForm] = questionSnippetForms;

    const {
      contentPage: { id },
    } = firstContentSnippetForm;

    setNewCondition((currentLocation) => ({
      ...currentLocation,
      contentPageId: id,
    }));
  }, [questionSnippetForms]);

  useEffect(() => {
    if (!selectedConditionQuestionItem) {
      return;
    }

    const {
      id,
      contentPage: { contentGroupId },
    } = selectedConditionQuestionItem;

    handleGetLocations({
      contentGroupId,
    });

    setNewCondition((currentCondition) => ({
      ...currentCondition,
      contentSnippetQuestionId: id,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConditionQuestionItem]);

  const { contentPageId, minValue, maxValue } = newCondition;

  const isSaveButtonDisabled = Boolean(!questionTitle) || Boolean(!minValue) || Boolean(!maxValue);

  return (
    <Form onSubmit={handleSaveCondition}>
      {selectedConditionQuestionItem && questionLocations.length ? (
        <div className="c-action">
          <Spacing size="2x-large" />
          <div className="c-action__item c-action__item__slider h-flex">
            <div className="c-action__item__slider--item">
              <Label additionalClassNames="c-action__item__label h-flex" forId="slider-min" labelText="Min value" isRequired>
                <Tooltip placement="top" text="The max value of slider" />
              </Label>
              <Input id="slider-min" type="number" value={minValue} onChange={handleMinValueChange} />
            </div>
            <div className="c-action__item__slider--item">
              <Label additionalClassNames="c-action__item__label h-flex" forId="slider-max" labelText="Max value" isRequired>
                <Tooltip placement="left" text="The min value of slider" />
              </Label>
              <Input id="slider-max" type="number" value={maxValue} onChange={handleMaxValueChange} />
            </div>
          </div>
          <Spacing size="2x-large" />
          <div className="c-action__item h-flex">
            <Label additionalClassNames="c-action__item__label h-flex" forId="question location" labelText="Question location">
              <Tooltip placement="left" text="This action will execute if the radio selection is as selected below" />
            </Label>
            <RadioButtonGroup
              items={questionLocations}
              name="location"
              selectedValue={contentPageId ? contentPageId.toString() : ''}
              onChange={handleQuestionLocationInputChange}
            />
          </div>
          <Spacing size="2x-large" />
          <div className="c-action__item h-flex">
            <Label additionalClassNames="c-action__item__label h-flex" forId="template" labelText="Template">
              <Tooltip placement="left" text="Save condition to reuse later" />
            </Label>
            <div className="c-action__item__checkbox h-flex">
              <Checkbox
                id="template"
                isChecked={isTemplateChecked}
                labelText="Save conditional configuration for reuse"
                name="template"
                onChange={handleIsTemplateChecked}
              />
            </div>
          </div>
        </div>
      ) : null}
      {selectedConditionQuestionItem ? (
        <FormButtons
          additionalClassNames="c-form-buttons__action-tab"
          isSaveButtonDisabled={isSaveButtonDisabled}
          onClose={onCloseSidebar}
        />
      ) : null}
    </Form>
  );
};

QuestionConditionSliderNew.propTypes = {
  args: PropTypes.shape({
    actionWrapper: WRAPPER_PROP_TYPE,
    activePageId: PropTypes.number,
    conditionAction: PropTypes.string,
    onAddActionCompleted: PropTypes.func,
    onAddConditionCompleted: PropTypes.func,
    onAddQuestionConditionLocations: PropTypes.func,
    onAddSelectedQuestionItem: PropTypes.func,
    onCloseSidebar: PropTypes.func,
    questionAction: QUESTION_ACTION_PROP_TYPE,
    questionLocations: QUESTION_LOCATION_PROP_TYPE,
    questionTitle: PropTypes.string,
    selectedConditionQuestionItem: SELECTED_CONDITION_QUESTION_PROP_TYPE,
  }),
};

export { QuestionConditionSliderNew };
